import { alpha, styled } from "@mui/material/styles";
import { Card, Typography, Button, Box, Stack, Chip } from "@mui/material";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";

// Prevent React warning: bundleType prop leaking to DOM
const StyledIcon = styled("div", {
  shouldForwardProp: (prop) => prop !== "bundleType" && prop !== "colors",
})(({ theme, colors }) => ({
  margin: "0 auto",
  display: "grid",
  placeItems: "center",
  borderRadius: 14,
  width: theme.spacing(9),
  height: theme.spacing(9),
  color: colors.primary,
  background: `linear-gradient(135deg, ${colors.primary}18 0%, ${colors.secondary}10 100%)`,
  border: `1px solid ${colors.primary}30`,
  boxShadow: `0 10px 26px ${colors.primary}14`,
  transition: "transform 160ms ease, box-shadow 160ms ease",
  [theme.breakpoints.down("sm")]: {
    width: theme.spacing(8),
    height: theme.spacing(8),
  },
}));

export default function AppBundleWidget({
  title,
  icon,
  data = [],
  amount,
  reference,
  article,
  setOpenDialog,
  setReference,
  setAmount,
  currentUserCategory,
  ownedBundles = [],
  onSwitch,
  sx,
}) {
  const isFreeBundle = title === "Free";

  const isCurrentBundle =
    (currentUserCategory || "").toLowerCase() === (title || "").toLowerCase();

  const ownedBundle = ownedBundles.find(
    (b) => (b?.bundleType || "").toLowerCase() === (title || "").toLowerCase()
  );

  const isOwned = !!ownedBundle;
  const isExpired = !!ownedBundle?.isExpired;
  const expirationDate = ownedBundle?.expirationDate;
  const daysRemaining = ownedBundle?.daysRemaining || 0;

  // Helper function to get expiration status message
  const getExpirationStatusMessage = () => {
    if (isExpired) {
      return "⚠️ Expired";
    }

    if (daysRemaining === 0) {
      return "⚠️ Expires today";
    }

    if (daysRemaining === 1) {
      return "⏰ Expires tomorrow";
    }

    if (daysRemaining <= 7) {
      return `⏰ Expires in ${daysRemaining} days`;
    }

    if (daysRemaining <= 30) {
      return `✓ ${daysRemaining} days remaining`;
    }

    return "✓ Active";
  };

  // Determine button state and text
  let buttonText = "Purchase";
  let buttonDisabled = false;
  let buttonVariant = "contained";

  if (isCurrentBundle) {
    buttonText = "Current plan";
    buttonDisabled = true;
    buttonVariant = "outlined";
  } else if (isOwned && !isExpired) {
    buttonText = "Switch to this plan";
    buttonDisabled = false;
    buttonVariant = "outlined";
  } else if (isOwned && isExpired) {
    buttonText = "Renew";
    buttonDisabled = false;
    buttonVariant = "contained";
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const colors = { primary: "#ff715b", secondary: "#e65d47" };

  const handleClick = () => {
    if (buttonDisabled) return;

    // Switch directly if owned + not expired
    if (isOwned && !isExpired && !isCurrentBundle) {
      onSwitch && onSwitch(reference);
      return;
    }

    // Purchase/Renew opens dialog
    setOpenDialog(true);
    setReference(reference);
    setAmount(amount);
  };

  return (
    <Card
      sx={[
        {
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "auto",
          textAlign: "left",
          background: isCurrentBundle
            ? `linear-gradient(135deg, ${colors.primary}14 0%, ${colors.secondary}08 100%)`
            : `linear-gradient(135deg, ${colors.primary}07 0%, ${colors.secondary}03 100%)`,
          border: isCurrentBundle
            ? `2px solid ${colors.primary}55`
            : `1px solid ${colors.primary}22`,
          boxShadow: isCurrentBundle
            ? `0 12px 28px ${colors.primary}24, 0 6px 18px rgba(0,0,0,0.10)`
            : `0 10px 24px ${colors.primary}14, 0 4px 14px rgba(0,0,0,0.08)`,
          transition:
            "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
          "&:hover": {
            transform: isCurrentBundle ? "none" : "translateY(-3px)",
            boxShadow: isCurrentBundle
              ? `0 12px 28px ${colors.primary}24, 0 6px 18px rgba(0,0,0,0.10)`
              : `0 16px 36px ${colors.primary}18, 0 10px 22px rgba(0,0,0,0.12)`,
            borderColor: isCurrentBundle
              ? `${colors.primary}55`
              : `${colors.primary}35`,
          },
        },
        sx,
      ]}
    >
      {/* Badge */}
      {(article || isCurrentBundle) && (
        <Chip
          label={isCurrentBundle ? "Current" : article}
          color={isCurrentBundle ? "success" : "primary"}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            fontWeight: 700,
            borderRadius: 1,
          }}
        />
      )}

      {/* Header */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <StyledIcon bundleType={title} colors={colors}>
          {icon}
        </StyledIcon>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
            {title}
          </Typography>

          {/* Price */}
          <Stack
            direction="row"
            alignItems="baseline"
            spacing={1}
            sx={{ mt: 0.5 }}
          >
            <Typography variant="h4" sx={{ lineHeight: 1 }}>
              {amount}
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 0.5, color: "text.secondary" }}
              >
                Coins
              </Typography>
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              /month
            </Typography>
          </Stack>
        </Box>
      </Stack>

      {/* Expiration Info (compact) */}
      {isOwned && expirationDate && (
        <Box
          sx={{
            marginBottom: "15px",
            padding: "10px",
            borderRadius: "6px",
            background:
              isExpired || daysRemaining === 0
                ? "rgba(244, 67, 54, 0.1)"
                : daysRemaining <= 7
                ? "rgba(255, 152, 0, 0.1)"
                : "rgba(76, 175, 80, 0.1)",
            border:
              isExpired || daysRemaining === 0
                ? "1px solid rgba(244, 67, 54, 0.3)"
                : daysRemaining <= 7
                ? "1px solid rgba(255, 152, 0, 0.3)"
                : "1px solid rgba(76, 175, 80, 0.3)",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontWeight: "600",
              color:
                isExpired || daysRemaining === 0
                  ? "#d32f2f"
                  : daysRemaining <= 7
                  ? "#f57c00"
                  : "#388e3c",
              marginBottom: "4px",
            }}
          >
            {getExpirationStatusMessage()}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              fontSize: "11px",
            }}
          >
            {isExpired
              ? `Expired on ${formatDate(expirationDate)}`
              : `Valid until ${formatDate(expirationDate)}`}
          </Typography>
        </Box>
      )}

      {/* Features (NO fixed height, compact spacing) */}
      <Stack spacing={1} sx={{ mb: 2 }}>
        {data
          .map((x) => (typeof x === "string" ? x.trim() : ""))
          .filter(Boolean)
          .map((trimmedX, index) => {
            const isInclude = trimmedX.toLowerCase().startsWith("include");
            const iconColor = isInclude ? "#2e7d32" : "#d32f2f";
            const textDecoration = isInclude ? "none" : "line-through";
            const text = isInclude
              ? trimmedX.substring("include".length).trim()
              : trimmedX;
            const IconComponent = isInclude
              ? DoneOutlinedIcon
              : ClearOutlinedIcon;

            return (
              <Stack
                key={index}
                direction="row"
                spacing={1}
                alignItems="flex-start"
              >
                <IconComponent
                  sx={{
                    mt: "2px",
                    borderRadius: "50%",
                    fontSize: 16,
                    color: iconColor,
                    background: `linear-gradient(135deg, ${alpha(
                      iconColor,
                      0.06
                    )} 0%, ${alpha(iconColor, 0.18)} 100%)`,
                    height: 22,
                    width: 22,
                    p: "3px",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.35,
                    color: isInclude
                      ? "text.secondary"
                      : "rgba(20, 43, 58, 0.55)",
                    textDecoration,
                  }}
                >
                  {text}
                </Typography>
              </Stack>
            );
          })}
      </Stack>

      {/* CTA (pinned bottom, but no huge empty padding) */}
      {!isFreeBundle && (
        <Box sx={{ mt: "auto" }}>
          <Button
            fullWidth
            onClick={handleClick}
            variant={buttonVariant}
            size="large"
            disabled={buttonDisabled}
            sx={{
              borderRadius: 1.5,
              py: 1.1,
              fontSize: 15,
              fontWeight: 700,
              textTransform: "none",
              ...(buttonVariant === "contained"
                ? {
                    background: buttonDisabled
                      ? "rgba(0, 0, 0, 0.12)"
                      : `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                    color: buttonDisabled ? "rgba(0, 0, 0, 0.26)" : "white",
                    boxShadow: buttonDisabled
                      ? "none"
                      : `0 10px 22px ${colors.primary}30`,
                    "&:hover": {
                      background: buttonDisabled
                        ? "rgba(0, 0, 0, 0.12)"
                        : `linear-gradient(135deg, ${colors.secondary}, ${colors.primary})`,
                    },
                  }
                : {}),
              "&.Mui-disabled": {
                background: "rgba(0, 0, 0, 0.10)",
                color: "rgba(0, 0, 0, 0.28)",
              },
            }}
          >
            {buttonText}
          </Button>
        </Box>
      )}
    </Card>
  );
}
